import { useSyncExternalStore } from "react";

/**
 * The single notice at the bottom of the screen.
 *
 * It lives outside React on purpose: the query cache reports failed refreshes from where no
 * component exists, and it must be able to say so through the same channel.
 */

const DURATION_MS = 2600;

interface Toast {
  message: string;
  isError: boolean;
}

let current: Toast | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function notify(message: string, isError = false): void {
  current = { message, isError };
  emit();
  clearTimeout(timer);
  timer = setTimeout(() => {
    current = null;
    emit();
  }, DURATION_MS);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function ToastHost() {
  const toast = useSyncExternalStore(subscribe, () => current);
  if (!toast) return null;
  return (
    <div className="cs-toast" role="status" aria-live="polite">
      {/* An error is not announced with the same green dot as a success. */}
      <span className="cs-toast-dot" data-error={toast.isError ? "true" : undefined} />
      {toast.message}
    </div>
  );
}
